import React, { useState, useRef } from 'react';
import { useDelivery } from '../hooks/useDelivery';
import { Button, Table, TableBody, TableCell, TableHead, TableRow, Container, Box, Pagination, Alert, CircularProgress } from '@mui/material';
import { supabase } from '../../configs/supabase';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/ja';

// プラグインを使用するための設定
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('ja');


export default function DeliveryList() {
  const { deliveries, page, totalPages, setPage, fetchDeliveries, loading, error } = useDelivery();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [uploadLoading, setUploadLoading] = useState<boolean>(false);

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setUploadLoading(true);
    let message = "";
    const fileArray = Array.from(files);
    let allSuccess = true; // すべて成功したかどうかを追跡

    // バケットが存在するか確認
    const { data: bucketList, error: listError } = await supabase.storage.listBuckets();
    if (listError) {
      message += `バケットリストの取得失敗: ${listError.message}<br />`;
      allSuccess = false;
      setUploadLoading(false);
      return;
    }

    for (const file of fileArray) {
      const match = file.name.match(/^(\d+)_delivery_(\d{8})_(\d{2})_(\d+)\.pdf$/);
      if (!match) {
        message += `ファイル名が不正です: ${file.name}<br />`;
        allSuccess = false;
        continue;
      }

      const [, , deliveryDate, deliveryTime, customerId] = match;

      const bucketExists = bucketList.some(bucket => bucket.name === customerId);


      // バケットが存在しない場合は作成
      if (!bucketExists) {
        const { error: bucketError } = await supabase.storage.createBucket(customerId, { public: true });
        if (bucketError) {
          message += `バケット作成失敗: ${file.name} - ${bucketError.message}<br />`;
          allSuccess = false;
          continue;
        }
      }

      // deliveryテーブルを確認して既存のファイル名を取得
      const { data: existingDeliveries, error: fetchError } = await supabase
        .from('delivery')
        .select('pdf_link')
        .eq('delivery_date', deliveryDate)
        .eq('delivery_time', parseInt(deliveryTime, 10))
        .eq('customer_id', parseInt(customerId, 10));

      let fileName;
      if (fetchError) {
        message += `データベースの確認失敗: ${file.name} - ${fetchError.message}<br />`;
        allSuccess = false;
        continue;
      } else if (existingDeliveries && existingDeliveries.length > 0) {
        // 既存のレコードがある場合、pdf_linkからファイル名をパース
        const existingLink = existingDeliveries[0].pdf_link;
        const match = existingLink.match(/\/([^/]+)\.pdf$/);
        fileName = match ? match[1] + '.pdf' : `${uuidv4()}.pdf`;
      } else {
        // 既存のレコードがない場合、乱数でファイル名を生成
        fileName = `${uuidv4()}.pdf`;
      }

      // Supabaseのバケットにファイルをアップロード（上書き）
      const { error: uploadError } = await supabase.storage
        .from(customerId)
        .upload(fileName, file, { upsert: true });

      if (uploadError) {
        message += `アップロード失敗: ${file.name} - ${uploadError.message}<br />`;
        allSuccess = false;
        continue;
      }

      // ファイルの公開URLを取得
      const { data } = supabase.storage
        .from(customerId)
        .getPublicUrl(fileName);

      if (!data || !data.publicUrl) {
        message += `公開URL取得失敗: ${file.name}<br />`;
        allSuccess = false;
        continue;
      }

      const publicURL = data.publicUrl;

      // deliveryテーブルにデータを追加
      const { error: insertError } = await supabase
        .from('delivery')
        .upsert({
          delivery_date: deliveryDate,
          delivery_time: parseInt(deliveryTime, 10),
          pdf_link: publicURL,
          customer_id: parseInt(customerId, 10),
          upload_date: dayjs().tz('Asia/Tokyo').format('YYYY-MM-DD HH:mm:ss')
        }, { onConflict: 'pdf_link' });

      if (insertError) {
        message += `データベースへの追加失敗: ${file.name} - ${insertError.message}<br />`;
        allSuccess = false;
      }
    }

    if (allSuccess) {
      message = "正常にアップロードが完了しました。";
    } else {
      message += "アップロード処理が終了しました。";
    }

    setUploadLoading(false);
    setImportMessage(message);
    fetchDeliveries(page);
  };

  return (
    <Container>
      <h2>納品管理</h2>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button variant="contained" color="primary" onClick={() => fileInputRef.current?.click()}>
          pdfインポート
        </Button>
        <input
          type="file"
          accept=".pdf"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handlePdfUpload}
          multiple
        />
      </Box>
      {importMessage && (
        <Alert severity={importMessage.includes("正常にアップロードが完了しました。") ? "success" : "error"} sx={{ mb: 2 }}>
          <span dangerouslySetInnerHTML={{ __html: importMessage }} />
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {uploadLoading && (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
        </Box>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>納品ID</TableCell>
              <TableCell>納品日</TableCell>
              <TableCell>納品時間</TableCell>
              <TableCell>PDFリンク</TableCell>
              <TableCell>顧客ID<br />顧客名</TableCell>
              <TableCell>最新アップロード時刻</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {deliveries.map((delivery, index) => (
              <TableRow key={index}>
                <TableCell>{delivery.id}</TableCell>
                <TableCell>{delivery.delivery_date}</TableCell>
                <TableCell>{delivery.delivery_time}</TableCell>
                <TableCell><a href={delivery.pdf_link} target="_blank" rel="noopener noreferrer">PDF</a></TableCell>
                <TableCell>{delivery.customer_id}<br />{delivery.customer.name}</TableCell>
                <TableCell>{dayjs(delivery.upload_date).format('YYYY-MM-DD HH:mm:ss')}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination count={totalPages} page={page} onChange={handlePageChange} />
      </Box>
    </Container>
  );
}
